/**
 * Hook for triggering patch validation on Constantine findings.
 * Calls POST /constantine/validate with selected risk keys.
 *
 * Tracks validation lifecycle states:
 * - Ready: no validation running, no results
 * - Running: validation job in progress (mutation pending or polling active)
 * - Validated: patch was validated successfully
 * - Insufficient: patch was insufficient to remediate the vulnerability
 * - Inconclusive: validation results were inconclusive
 */
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { useOsintContext } from '@/state/OsintContext';
import { ResourceType, Risk } from '@/types';

import { isConstantineFinding, isExploitable } from './useExploitVerification';

/** Validation lifecycle states for the button UI */
export type ValidateState =
  | 'ready'
  | 'running'
  | 'validated'
  | 'insufficient'
  | 'inconclusive';

/** Check if a risk has been successfully exploited (required before validation) */
export function canValidate(risk: Risk): boolean {
  return isConstantineFinding(risk) && isExploitable(risk);
}

/** Check if a risk already has validation results via tags */
export function hasValidationResults(risk: Risk): boolean {
  const tags = risk.tags ?? [];
  return tags.some(
    t =>
      t === 'patch-validated' ||
      t === 'patch-insufficient' ||
      t === 'validation-attempted'
  );
}

/** Check if a risk has a validated patch */
export function isPatchValidated(risk: Risk): boolean {
  return (risk.tags ?? []).includes('patch-validated');
}

/** Check if patch was insufficient */
export function isPatchInsufficient(risk: Risk): boolean {
  const tags = risk.tags ?? [];
  return (
    tags.includes('patch-insufficient') && !tags.includes('patch-validated')
  );
}

/**
 * Determine the aggregate validation state for a set of risks.
 * - If any risk has a validated patch, the aggregate is 'validated'
 * - If all have results and some insufficient, 'insufficient'
 * - If all have results but none validated/insufficient, 'inconclusive'
 * - If none have results, 'ready'
 */
export function getValidateState(risks: Risk[]): ValidateState {
  if (risks.length === 0) return 'ready';

  const allHaveResults = risks.every(hasValidationResults);

  if (risks.some(isPatchValidated)) return 'validated';

  if (allHaveResults && risks.some(isPatchInsufficient)) return 'insufficient';

  if (allHaveResults) return 'inconclusive';

  return 'ready';
}

/** Returns label, icon, tooltip, and className for the validate button based on current state */
export function getValidateButtonConfig(state: ValidateState): {
  label: string;
  tooltip: string;
  icon: ReactNode;
  className: string;
} {
  switch (state) {
    case 'running':
      return {
        label: 'Validating...',
        tooltip: 'Patch validation is in progress',
        icon: <ArrowPathIcon className="size-4 motion-safe:animate-spin" />,
        className: 'bg-chariot-status-info text-white opacity-70 cursor-wait',
      };
    case 'validated':
      return {
        label: 'Patch Validated',
        tooltip: 'The patch was validated successfully',
        icon: <CheckCircleIcon className="size-4" />,
        className: 'bg-emerald-600 text-white',
      };
    case 'insufficient':
      return {
        label: 'Patch Insufficient',
        tooltip: 'The patch was insufficient to remediate the vulnerability',
        icon: <XCircleIcon className="size-4" />,
        className: 'bg-chariot-status-critical text-white',
      };
    case 'inconclusive':
      return {
        label: 'Inconclusive',
        tooltip:
          'Validation inconclusive — the exploit did not produce a replayable test recipe',
        icon: <ExclamationTriangleIcon className="size-4" />,
        className: 'bg-chariot-status-low text-white',
      };
    case 'ready':
    default:
      return {
        label: 'Validate Patch',
        tooltip: 'Run patch validation against exploited findings',
        icon: <WrenchScrewdriverIcon className="size-4" />,
        className: '',
      };
  }
}

export function useValidateVerification(onComplete?: () => void) {
  const { isOsint } = useOsintContext();
  const axios = useAxios();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    };
  }, []);

  const isRunning = isSubmitting || isPolling;

  const handleValidate = useCallback(
    async (vulnerabilities: Risk[]): Promise<void> => {
      const validatableFindings = vulnerabilities.filter(canValidate);

      if (validatableFindings.length === 0) {
        toast.warning('No exploited Constantine findings selected');
        return;
      }

      const riskKeys = validatableFindings.map(r => r.key);
      const toastId = 'validate-verification';

      toast.loading(
        `Starting patch validation for ${riskKeys.length} finding${riskKeys.length > 1 ? 's' : ''}...`,
        { id: toastId }
      );

      setIsSubmitting(true);

      try {
        const validateUrl = isOsint
          ? '/constantine/validate?osint=true'
          : '/constantine/validate';
        await axios.post(validateUrl, {
          risk_keys: riskKeys,
        });

        toast.success(
          `Patch validation started for ${riskKeys.length} finding${riskKeys.length > 1 ? 's' : ''}`,
          { id: toastId, duration: 5000 }
        );

        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy(ResourceType.Job),
        });
        queryClient.invalidateQueries({
          queryKey: getQueryKey.genericSearch(''),
        });

        // Start polling for risk updates
        setIsPolling(true);

        // Clear any existing polling
        if (pollingRef.current) clearInterval(pollingRef.current);
        if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);

        pollingRef.current = setInterval(() => {
          queryClient.invalidateQueries({
            queryKey: getQueryKey.getMy(ResourceType.Risk),
          });
        }, 5000);

        // Stop polling after 30 minutes
        pollingTimeoutRef.current = setTimeout(() => {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setIsPolling(false);
        }, 1800000);

        onComplete?.();
      } catch (error) {
        console.error('[useValidateVerification] Failed:', error);
        toast.error('Failed to start patch validation', { id: toastId });
      } finally {
        setIsSubmitting(false);
      }
    },
    [axios, isOsint, onComplete]
  );

  /** Stop polling early (e.g., when results arrive via risk refresh) */
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  return { handleValidate, isRunning, stopPolling };
}
